import { useState, type ReactNode } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ExplainedValue } from '@/components/ExplainedValue';
import { ExternalLink } from '@/components/ExternalLink';
import { StatusBadge } from '@/components/StatusBadge';
import { useFormatTaskRef } from '@/lib/current-project';
import { useBoardPath } from '@/navigation/project-paths';
import { sharedPrParent, type TaskDetail, type TaskKind, type TaskPr, type TaskSize, type TaskSuggestedField, type TaskType, type TaskUpdateInput } from '@shared/types/tasks';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusSelect } from '@/components/StatusSelect';
import { TypeChip } from './task-chips';
import { TASK_KIND_CHIPS, TASK_PR_STATUSES, TASK_SIZE_CHIPS, prLabel, taskTypeOf } from './task-types';
import { ChipSelect, type ChipOption } from '@/components/ChipSelect';

type Patch = (input: TaskUpdateInput, optimistic?: Partial<TaskDetail>) => Promise<boolean>;

/** Without the fields a person just set or confirmed: Jev's mark goes (T-17). */
function unmark(task: TaskDetail, ...fields: TaskSuggestedField[]): TaskSuggestedField[] {
  return (task.ai_suggested ?? []).filter((f) => !fields.includes(f));
}

/** The type's chip; a click picks another. Jev's suggestion shows its sparkle (T-17). */
export function TypePicker({ task, types, patch, children }: { task: TaskDetail; types: TaskType[]; patch: Patch; children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const type = taskTypeOf(types, task);
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" aria-label={`Type: ${type?.name ?? 'Task'}. Change it`} className="inline-flex rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {type ? <TypeChip type={type} suggested={task.ai_suggested?.includes('type')} className="px-2 py-1 text-xs" /> : null}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-0" align="start">
          <Command>
            <CommandList>
              <CommandGroup>
                {types.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={t.name}
                    onSelect={() => {
                      setOpen(false);
                      // Always saved, even the type it shows: a picked type is never replaced by Jev's suggestion.
                      void patch({ type_id: t.id }, { type_id: t.id, ai_suggested: unmark(task, 'type') });
                    }}
                  >
                    <TypeChip type={t} />
                    <span className="flex-1" />
                    {t.id === type?.id ? <Check className="size-4" /> : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {children}
    </>
  );
}

const NONE = 'none';

/** "Size" or "Kind" until set; then the shared chips (task-types). */
const SIZE_CHIPS: Record<TaskSize | typeof NONE, ChipOption> = { [NONE]: { label: 'Size', colour: 'gray' }, ...TASK_SIZE_CHIPS };
const KIND_CHIPS: Record<TaskKind | typeof NONE, ChipOption> = { [NONE]: { label: 'Kind', colour: 'gray' }, ...TASK_KIND_CHIPS };

/** XS to XL, or none, as a chip (ChipSelect: the chip is the button). */
export function SizeSelect({ task, patch }: { task: TaskDetail; patch: Patch }) {
  return (
    <ChipSelect
      value={task.size ?? NONE}
      options={SIZE_CHIPS}
      aria-label="Size"
      suggested={task.ai_suggested?.includes('size') ? 'Jev suggested this size: keep it, or pick another' : undefined}
      onValueChange={(v) => {
        const size = v === NONE ? null : (v as TaskSize);
        void patch({ size }, { size, ai_suggested: unmark(task, 'size') });
      }}
    />
  );
}

/** Fix, feature, chore… as a chip; changing it clears Jev's suggestion mark, as the type does. */
export function KindSelect({ task, patch }: { task: TaskDetail; patch: Patch }) {
  return (
    <ChipSelect
      value={task.kind ?? NONE}
      options={KIND_CHIPS}
      aria-label="Kind"
      suggested={task.ai_suggested?.includes('kind') ? 'Jev suggested this kind: keep it, or pick another' : undefined}
      onValueChange={(v) => {
        const kind = v === NONE ? null : (v as TaskKind);
        void patch({ kind }, { kind, ai_suggested: unmark(task, 'kind') });
      }}
    />
  );
}

/**
 * Keep Jev's suggestions (T-17): confirms every field still marked as Jev's, so it never changes
 * them after a big edit. Shown only while there are some; picking a field yourself confirms it too.
 */
export function KeepSuggestions({ task, patch, fields }: { task: TaskDetail; patch: Patch; fields: TaskSuggestedField[] }) {
  const marked = (task.ai_suggested ?? []).filter((f) => fields.includes(f));
  if (!marked.length) return null;
  const names = marked.join(', ').replace(/, ([^,]*)$/, ' and $1');
  return (
    <Button
      variant="ghost"
      size="xs"
      data-hint={`Jev suggested the ${names}: keep them, or pick others`}
      onClick={() => void patch({ confirm_suggested: marked }, { ai_suggested: unmark(task, ...marked) })}
    >
      <Check className="size-3.5" />
      Keep
    </Button>
  );
}

/** Each pull request with its status; the task loop sets them through the API, people here. */
export function PrList({
  task,
  patch,
  addButton,
  defaultOpen = false,
  onOpenChange,
}: {
  task: TaskDetail;
  patch: Patch;
  /** The button that opens the address box; left out, there's no box (only the list). */
  addButton?: (props: { label: string; showLabel: boolean }) => ReactNode;
  /** Open the address box as soon as it appears (picked from the properties' + menu). */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const formatRef = useFormatTaskRef();
  const boardPath = useBoardPath();
  const [open, setOpenState] = useState(defaultOpen);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [url, setUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const save = (prs: TaskPr[]) => patch({ prs }, { prs });
  const valid = /^https?:\/\/\S+$/i.test(url.trim());
  // The address stays in the box until it's saved, so a refused one can be corrected.
  const add = async () => {
    if (!valid || adding) return;
    const next = url.trim();
    if (task.prs.some((p) => p.url === next)) {
      setOpen(false);
      setUrl('');
      return;
    }
    setAdding(true);
    const ok = await save([...task.prs, { url: next, status: 'open' }]);
    setAdding(false);
    if (ok) {
      setOpen(false);
      setUrl('');
    }
  };
  return (
    <>
      {/* The add box's button first: in a task's properties it's the property's icon. */}
      {addButton ? (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{addButton({ label: task.prs.length ? 'Add another pull request' : 'Add PR', showLabel: !task.prs.length })}</PopoverTrigger>
        <PopoverContent className="w-80 space-y-2 p-3" align="start">
          <form
            noValidate
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void add();
            }}
          >
            <Input
              value={url}
              autoFocus
              placeholder="https://github.com/…/pull/42"
              aria-label="Pull request address"
              onChange={(e) => setUrl(e.target.value)}
            />
            <Button type="submit" size="sm" disabled={!valid} loading={adding} loadingText="Adding…">
              <Plus className="size-4" />
              Add
            </Button>
          </form>
        </PopoverContent>
      </Popover>
      ) : null}
      {task.prs.map((pr) => {
        // SF-T-154: a PR the parent shares is the parent's to change; here it only says where it lives.
        const home = sharedPrParent(task, pr.url);
        if (home) {
          return (
            <span key={pr.url} className="inline-flex h-7 items-center gap-1.5 rounded-full bg-muted px-2 text-sm">
              <ExternalLink href={pr.url} size="sm" data-hint={pr.url}>
                {prLabel(pr.url)}
              </ExternalLink>
              <StatusBadge size="sm" meta={TASK_PR_STATUSES[pr.status]} />
              <ExplainedValue
                className="text-xs text-muted-foreground"
                explanation={`One pull request for ${formatRef(home.number)}'s sub-tasks: its status, review and Merge are on ${formatRef(home.number)}.`}
              >
                Shared with
              </ExplainedValue>
              <Link to={boardPath(home.task_id)} className="text-xs hover:underline">
                {formatRef(home.number)}
              </Link>
            </span>
          );
        }
        return (
          <span key={pr.url} className="inline-flex h-7 items-center gap-1 rounded-full bg-muted pl-2 pr-0.5 text-sm">
            <ExternalLink href={pr.url} size="sm" data-hint={pr.url}>
              {prLabel(pr.url)}
            </ExternalLink>
            <StatusSelect
              size="sm"
              value={pr.status}
              statuses={TASK_PR_STATUSES}
              aria-label={`Status of ${prLabel(pr.url)}`}
              onValueChange={(status) => void save(task.prs.map((p) => (p.url === pr.url ? { ...p, status } : p)))}
            />
            <button
              type="button"
              aria-label={`Remove ${prLabel(pr.url)}`}
              className="flex size-5 items-center justify-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground"
              onClick={() => void save(task.prs.filter((p) => p.url !== pr.url))}
            >
              <X className="size-3" />
            </button>
          </span>
        );
      })}
    </>
  );
}
